/**
 * Compact statistics panel for a negotiation.
 *
 * Shows key metrics (versions, comments, resolution rate, response time)
 * and per-party activity bars. Displayed in the "show more" accordion
 * section of SendNegotiation.
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  BarChart3,
  ChevronDown,
  ChevronUp,
  MessageSquare,
  Clock,
  CheckCircle2,
  Users,
} from 'lucide-react'
import { useNegotiationStats } from '../../hooks/useNegotiations'

interface NegotiationStatsPanelProps {
  negotiationId: number
  alwaysExpanded?: boolean
}

export function NegotiationStatsPanel({ negotiationId, alwaysExpanded }: NegotiationStatsPanelProps) {
  const { t } = useTranslation()
  const { data: stats, isLoading } = useNegotiationStats(negotiationId)
  const [isExpanded, setIsExpanded] = useState(alwaysExpanded ?? false)

  if (isLoading || !stats) return null

  const resolutionPct = Math.round(stats.resolution_rate * 100)

  const expanded = alwaysExpanded || isExpanded

  return (
    <div className={alwaysExpanded ? '' : 'border-b border-neutral-100 dark:border-neutral-800'}>
      {!alwaysExpanded && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full px-3 py-1.5 flex items-center gap-1.5 text-[10px] font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-colors"
        >
          <BarChart3 size={10} />
          {t('negotiate.stats.title')}
          <span className="flex-1" />
          {isExpanded ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
        </button>
      )}

      {expanded && (
        <div className="px-3 pb-2.5 space-y-2.5 animate-fade-in">
          {/* Key metrics grid */}
          <div className="grid grid-cols-2 gap-1.5">
            <MetricCard
              icon={<Clock size={10} />}
              label={t('negotiate.stats.versions')}
              value={String(stats.total_versions)}
              sub={t('negotiate.stats.daysActive', { days: stats.days_since_created })}
            />
            <MetricCard
              icon={<MessageSquare size={10} />}
              label={t('negotiate.stats.comments')}
              value={String(stats.total_comments)}
              sub={`${stats.unresolved_comments} ${t('negotiate.stats.unresolved')}`}
            />
            <MetricCard
              icon={<CheckCircle2 size={10} />}
              label={t('negotiate.stats.resolutionRate')}
              value={`${resolutionPct}%`}
              sub={`${stats.resolved_comments}/${stats.total_comments}`}
            />
            <MetricCard
              icon={<Users size={10} />}
              label={t('negotiate.stats.readiness')}
              value={`${stats.ready_count}/${stats.total_parties}`}
              sub={t('negotiate.stats.partiesReady')}
            />
          </div>

          {/* Resolution rate bar */}
          <div>
            <div className="h-1.5 w-full bg-neutral-100 dark:bg-neutral-800 rounded-full overflow-hidden">
              <div
                className="h-full bg-emerald-500 rounded-full transition-all duration-500"
                style={{ width: `${resolutionPct}%` }}
              />
            </div>
          </div>

          {/* Response time */}
          {stats.avg_response_time_hours != null && (
            <div className="flex items-center justify-between text-[10px] text-neutral-500 dark:text-neutral-400">
              <span>{t('negotiate.stats.avgResponseTime')}</span>
              <span className="font-medium text-neutral-700 dark:text-neutral-300">
                {formatHours(stats.avg_response_time_hours, t)}
              </span>
            </div>
          )}

          {/* Per-party activity */}
          {stats.party_activity.length > 0 && (
            <div className="space-y-1">
              <p className="text-[10px] font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">
                {t('negotiate.stats.partyActivity')}
              </p>
              {stats.party_activity.map((party) => {
                const maxCount = stats.party_activity[0]?.comment_count || 1
                const pct = Math.round((party.comment_count / maxCount) * 100)
                return (
                  <div key={party.tag_name} className="flex items-center gap-1.5">
                    <div
                      className="w-1.5 h-1.5 rounded-full shrink-0"
                      style={{ backgroundColor: party.color }}
                    />
                    <span className="text-[10px] text-neutral-600 dark:text-neutral-400 truncate w-16">
                      #{party.tag_name}
                    </span>
                    <div className="flex-1 h-1 bg-neutral-100 dark:bg-neutral-800 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{ width: `${pct}%`, backgroundColor: party.color }}
                      />
                    </div>
                    <span className="text-[10px] text-neutral-500 dark:text-neutral-400 tabular-nums w-5 text-right">
                      {party.comment_count}
                    </span>
                  </div>
                )
              })}
            </div>
          )}

          {/* Version cadence */}
          {stats.avg_days_between_versions > 0 && (
            <div className="flex items-center justify-between text-[10px] text-neutral-500 dark:text-neutral-400">
              <span>{t('negotiate.stats.versionCadence', 'Version cadence')}</span>
              <span className="font-medium text-neutral-700 dark:text-neutral-300">
                {t('negotiate.stats.days', { count: Math.round(stats.avg_days_between_versions) })}
              </span>
            </div>
          )}

          {/* Most active / fastest responder */}
          <div className="space-y-0.5 text-[10px] text-neutral-400 dark:text-neutral-500">
            {stats.most_active_party && (
              <div className="flex items-center justify-between">
                <span>{t('negotiate.stats.mostActive')}</span>
                <strong className="text-neutral-600 dark:text-neutral-300">{stats.most_active_party}</strong>
              </div>
            )}
            {stats.fastest_responder && (
              <div className="flex items-center justify-between">
                <span>{t('negotiate.stats.fastest')}</span>
                <strong className="text-neutral-600 dark:text-neutral-300">{stats.fastest_responder}</strong>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// ── Helpers ──────────────────────────────────────────────

function MetricCard({
  icon,
  label,
  value,
  sub,
}: {
  icon: React.ReactNode
  label: string
  value: string
  sub: string
}) {
  return (
    <div className="p-1.5 rounded-lg bg-neutral-50 dark:bg-neutral-800/50">
      <div className="flex items-center gap-1 text-neutral-400 dark:text-neutral-500 mb-0.5">
        {icon}
        <span className="text-[9px] uppercase tracking-wide truncate">{label}</span>
      </div>
      <p className="text-sm font-semibold text-neutral-800 dark:text-neutral-200 tabular-nums">
        {value}
      </p>
      <p className="text-[9px] text-neutral-400 dark:text-neutral-500 truncate">{sub}</p>
    </div>
  )
}

function formatHours(hours: number, t: (key: string, opts?: Record<string, unknown>) => string): string {
  if (hours < 1) {
    return t('negotiate.stats.minutes', { count: Math.round(hours * 60) })
  }
  if (hours < 24) {
    return t('negotiate.stats.hours', { count: Math.round(hours) })
  }
  return t('negotiate.stats.days', { count: Math.round(hours / 24) })
}
